import React, { useCallback, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useParams } from 'react-router-dom';

import { TableRow, TableCell, IconButton, Divider, MoreVertIcon } from '@occc-app/design-system';
import { RetailerNetworkLocationGroupFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkUser } from '@occc-app/App/PolicyBasedAccessControl/userActions';

type MenuItem = 'locationGroupDetails' | 'removeUserFromLocationGroup';

type RetailerNetworkUserDetailsLocationGroupsListItemProps = {
  locationGroup: RetailerNetworkLocationGroupFragment;
  handleOpenLocationGroupDetailsPage: (locationGroupUuid: string) => void;
  handleRemoveUserFromLocationGroup: (locationGroupUuid: string) => void;
  isCurrentUserPermittedToDisassociate: boolean;
};

export const RetailerNetworkUserDetailsLocationGroupsTableRow = ({
  locationGroup,
  handleOpenLocationGroupDetailsPage,
  handleRemoveUserFromLocationGroup,
  isCurrentUserPermittedToDisassociate,
}: RetailerNetworkUserDetailsLocationGroupsListItemProps) => {
  const { networkId } = useParams<{ networkId: string }>();
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (locationGroup: RetailerNetworkLocationGroupFragment) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(locationGroup.uuid);
    };

  const handleClose = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const onMenuNavigationClick = (itemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (itemName === 'locationGroupDetails') {
      handleOpenLocationGroupDetailsPage(locationGroup.uuid);
    }

    if (itemName === 'removeUserFromLocationGroup') {
      handleRemoveUserFromLocationGroup(locationGroup.uuid);
    }

    handleClose(event);
  };

  return (
    <TableRow
      key={locationGroup.uuid}
      onClick={() => handleOpenLocationGroupDetailsPage(locationGroup.uuid)}
    >
      <TableCell>{locationGroup.name}</TableCell>
      <TableCell>{locationGroup.countOfLocations}</TableCell>

      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(locationGroup)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={locationGroup.uuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={onMenuNavigationClick('locationGroupDetails')}>
            View Location Group Details
          </MenuItem>

          <Can I={UpdateRetailerNetworkUser({ uuid: networkId || '' })}>
            {isCurrentUserPermittedToDisassociate && [
              <Divider key='divider' />,
              <MenuItem
                onClick={onMenuNavigationClick('removeUserFromLocationGroup')}
                key='disassociateUserFromLocationGroup'
              >
                Remove User from Location Group
              </MenuItem>,
            ]}
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
